import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Mail, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EmailSettingsTab({ getAuthHeaders }) {
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-settings`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      toast.error('Eroare la încărcarea setărilor email');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/email-settings`, settings, getAuthHeaders());
      toast.success(response.data.message);
      fetchSettings();
    } catch (error) {
      toast.error('Eroare la salvarea setărilor');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/test-email`, {}, getAuthHeaders());
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea email-ului de test');
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Setări Email (SMTP)
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configurați setările SMTP pentru trimiterea notificărilor email
          </p>
        </div>
        <Button
          onClick={handleTestEmail}
          variant="outline"
          disabled={testingEmail}
          data-testid="test-email-button"
        >
          {testingEmail ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Se trimite...
            </>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Testează Email
            </>
          )}
        </Button>
      </div>

      <div className="space-y-4 max-w-2xl">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="smtp_host">SMTP Host</Label>
            <Input
              id="smtp_host"
              value={settings.smtp_host}
              onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
              placeholder="smtp.acumbamail.com"
              data-testid="smtp-host-input"
            />
            <p className="text-xs text-gray-500 mt-1">Server SMTP (ex: smtp.acumbamail.com)</p>
          </div>

          <div>
            <Label htmlFor="smtp_port">SMTP Port</Label>
            <Input
              id="smtp_port"
              type="number"
              value={settings.smtp_port}
              onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
              placeholder="587"
              data-testid="smtp-port-input"
            />
            <p className="text-xs text-gray-500 mt-1">Port SMTP (uzual: 587 sau 465)</p>
          </div>
        </div>

        <div>
          <Label htmlFor="smtp_username">SMTP Username</Label>
          <Input
            id="smtp_username"
            value={settings.smtp_username}
            onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
            placeholder="your_email@example.com"
            data-testid="smtp-username-input"
          />
          <p className="text-xs text-gray-500 mt-1">Utilizator pentru autentificare SMTP</p>
        </div>

        <div>
          <Label htmlFor="smtp_password">SMTP Password</Label>
          <Input
            id="smtp_password"
            type="password"
            value={settings.smtp_password}
            onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
            placeholder="••••••••"
            data-testid="smtp-password-input"
          />
          <p className="text-xs text-gray-500 mt-1">Parola pentru autentificare SMTP</p>
        </div>

        <div>
          <Label htmlFor="smtp_from_email">From Email</Label>
          <Input
            id="smtp_from_email"
            type="email"
            value={settings.smtp_from_email}
            onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
            placeholder="noreply@pirifandistribution.ro"
            data-testid="smtp-from-email-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Adresa email de la care vor fi trimise notificările
          </p>
        </div>

        <div>
          <Label htmlFor="smtp_from_name">From Name</Label>
          <Input
            id="smtp_from_name"
            value={settings.smtp_from_name}
            onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
            placeholder="PIRIFAN DISTRIBUTION SRL"
            data-testid="smtp-from-name-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Numele care va apărea ca expeditor al email-urilor
          </p>
        </div>

        <div className="pt-4 flex gap-3">
          <Button
            onClick={handleSave}
            disabled={loading}
            data-testid="save-settings-button"
            style={{ backgroundColor: '#1a73e8' }}
          >
            {loading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Se salvează...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salvează Setări
              </>
            )}
          </Button>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h3 className="font-semibold text-blue-900 mb-2">📝 Notă Importantă</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• După salvarea setărilor, este recomandat să restartați backend-ul</li>
            <li>
              • Pentru Acumbamail, contactați suportul pentru a activa SMTP și a obține credențiale
            </li>
            <li>• Folosiți butonul Testează Email pentru a verifica configurația</li>
            <li>
              • Email-ul de test va fi trimis la adresa: <strong>doreloprisan@gmail.com</strong>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default EmailSettingsTab;
